import React, { useState, useEffect, useRef } from "react";
import type { Timeframe } from "../../types/Timeframe";
import ChartComponent from "../ChartComponent";
import Dropdown from "../Dropdown";
import { BiCheckboxChecked } from "react-icons/bi";
import { useAppSelector, useAppDispatch } from "../../store/hooks";
import {
    selectChartInputs,
    selectSelectedChart,
    selectChartLayout,
    setChartTimeframe,
    addChartStudy,
    removeChartStudy,
    setSelectedChart,
    setScreenLayout,
    duplicateChartToIndex,
} from "../../store/chartSlice";
import { Interval } from "@ocktra/shared-types";

interface ChartContainerProps {
    closeChart: () => void;
    instrument: string | null;
    width: number;
    height: number;
}

const STUDY_OPTIONS = [
    { id: "double_tops", name: "Double Tops" },
    { id: "double_bottoms", name: "Double Bottoms" },
    { id: "support_resistance", name: "Support & Resistance" },
    { id: "ground_level", name: "Ground Level" }
];

const TIMEFRAME_OPTIONS: { value: Interval; label: string }[] = [
    { value: "1min", label: "1 Minute" },
    { value: "5min", label: "5 Minutes" },
    { value: "15min", label: "15 Minutes" },
    { value: "60min", label: "1 Hour" },
    { value: "1day", label: "1 Day" },
    { value: "1week", label: "1 Week" },
    { value: "1month", label: "1 Month" }
];

const ChartWrapper: React.FC<ChartContainerProps> = ({ instrument, width, height }) => {
    const dispatch = useAppDispatch();

    // Get state from Redux
    const inputs = useAppSelector(selectChartInputs);
    const selectedChart = useAppSelector(selectSelectedChart);
    const { cols, rows, screenSplitIndex } = useAppSelector(selectChartLayout);

    const containerRef = useRef<HTMLDivElement>(null);
    const [cellSize, setCellSize] = useState({ width: width, height: height });

    // Update instrument when prop changes
    useEffect(() => {
        if (instrument && inputs[selectedChart]) {
            // Only update if different to avoid unnecessary updates
            if (inputs[selectedChart].instrument !== instrument) {
                dispatch(setChartTimeframe({ chartIndex: selectedChart, timeframe: inputs[selectedChart].timeframe }));
            }
        }
    }, [instrument, selectedChart, inputs, dispatch]);

    useEffect(() => {
        const container = containerRef.current;
        if (!container) return;

        const updateSize = () => {
            const rect = container.getBoundingClientRect();
            setCellSize({
                width: rect.width / cols,
                height: rect.height / rows,
            });
        };

        updateSize(); // initial

        const resizeObserver = new ResizeObserver(updateSize);
        resizeObserver.observe(container);

        return () => resizeObserver.disconnect();
    }, [cols, rows]);


    useEffect(() => {
        setCellSize({
            width: width,
            height: height-84,
        });
    }, [width, height]);

    // Screen split icons - simplified inline SVGs
    const SingleScreenIcon = () => (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
            <rect x="3" y="5" width="18" height="14" rx="2" stroke="currentColor" fill="none" strokeWidth="2" />
        </svg>
    );

    const DoubleScreenIcon = () => (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
            <rect x="3" y="5" width="8" height="14" rx="1" stroke="currentColor" fill="none" strokeWidth="2" />
            <rect x="13" y="5" width="8" height="14" rx="1" stroke="currentColor" fill="none" strokeWidth="2" />
        </svg>
    );

    const TripleScreenIcon = () => (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
            <rect x="2" y="5" width="6" height="14" rx="1" stroke="currentColor" fill="none" strokeWidth="1.5" />
            <rect x="9" y="5" width="6" height="14" rx="1" stroke="currentColor" fill="none" strokeWidth="1.5" />
            <rect x="16" y="5" width="6" height="14" rx="1" stroke="currentColor" fill="none" strokeWidth="1.5" />
        </svg>
    );

    const getScreenIcon = (index: number) => {
        switch (index) {
            case 0:
                return <SingleScreenIcon />;
            case 1:
                return <DoubleScreenIcon />;
            case 2:
                return <TripleScreenIcon />;
            default:
                return null;
        }
    }

    const setDefaultTemplate = async (template: any) => {
        // TODO: Implement with proper API call and Redux action
        console.log('Setting default template:', template);
        alert('Feature coming soon: Save as default template');
    }

    return (
        <>
            {
                !instrument ? <>
                    <div style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        height: '100%',
                        width: '100%',
                        background: 'linear-gradient(135deg, var(--color-surface) 0%, var(--color-gray-50) 100%)',
                        padding: 'var(--spacing-xl)',
                        position: 'relative',
                        overflow: 'hidden'
                    }}>
                        {/* Animated background elements */}
                        <div style={{
                            position: 'absolute',
                            width: '300px',
                            height: '300px',
                            borderRadius: '50%',
                            background: 'radial-gradient(circle, rgba(59, 130, 246, 0.1) 0%, transparent 70%)',
                            animation: 'pulse-slow 4s ease-in-out infinite',
                            top: '20%',
                            left: '10%'
                        }} />
                        <div style={{
                            position: 'absolute',

                            width: '200px',
                            height: '200px',
                            borderRadius: '50%',
                            background: 'radial-gradient(circle, rgba(139, 92, 246, 0.1) 0%, transparent 70%)',
                            animation: 'pulse-slow 3s ease-in-out infinite 1s',
                            bottom: '20%',
                            right: '15%'
                        }} />

                        {/* Main content */}
                        <div style={{
                            textAlign: 'center',
                            zIndex: 1,
                            animation: 'fade-in-up 0.8s ease-out',
                            maxWidth: '400px'
                        }}>
                            {/* Animated chart icon */}
                            <div style={{
                                animation: 'float 3s ease-in-out infinite',
                                marginBottom: 'var(--spacing-lg)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                            }}>
                                <svg
                                    width="120"
                                    height="120"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="var(--color-primary-600)"
                                    strokeWidth="1.5"
                                    style={{
                                        filter: 'drop-shadow(0 4px 12px rgba(59, 130, 246, 0.3))'
                                    }}
                                >
                                    <path d="M3 3v18h18" strokeLinecap="round" strokeLinejoin="round" />
                                    <path d="M18.7 8l-5.1 5.2-2.8-2.7L7 14.3" strokeLinecap="round" strokeLinejoin="round">
                                        <animate
                                            attributeName="stroke-dasharray"
                                            from="0 100"
                                            to="100 0"
                                            dur="2s"
                                            repeatCount="indefinite"
                                        />
                                    </path>
                                    <circle cx="18.7" cy="8" r="1" fill="var(--color-primary-600)">
                                        <animate
                                            attributeName="r"
                                            values="1;1.5;1"
                                            dur="2s"
                                            repeatCount="indefinite"
                                        />
                                    </circle>
                                </svg>
                            </div>

                            <h3 style={{
                                fontSize: '1.5rem',
                                fontWeight: '600',
                                color: 'var(--color-text-primary)',
                                margin: '0 0 var(--spacing-md) 0',
                                animation: 'fade-in 1s ease-out 0.3s both'
                            }}>
                                No Instrument Selected
                            </h3>

                            <p style={{
                                fontSize: '0.9375rem',
                                color: 'var(--color-text-secondary)',
                                margin: '0 0 var(--spacing-lg) 0',
                                lineHeight: 1.6,
                                animation: 'fade-in 1s ease-out 0.5s both'
                            }}>
                                Click on any instrument from the Market Watch to view its chart and start analyzing price movements
                            </p>

                            {/* Animated arrow pointer */}
                            <div style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: 'var(--spacing-sm)',
                                color: 'var(--color-primary-600)',
                                fontSize: '0.875rem',
                                fontWeight: '500',
                                animation: 'fade-in 1s ease-out 0.7s both, bounce-horizontal 2s ease-in-out infinite 2s'
                            }}>
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path d="M5 12h14M12 5l7 7-7 7" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                                <span>Select an instrument</span>
                            </div>
                        </div>

                        <style>{`
                                @keyframes pulse-slow {
                                    0%, 100% {
                                        opacity: 0.3;
                                        transform: scale(1);
                                    }
                                    50% {
                                        opacity: 0.6;
                                        transform: scale(1.1);
                                    }
                                }

                                @keyframes float {
                                    0%, 100% {
                                        transform: translateY(0px);
                                    }
                                    50% {
                                        transform: translateY(-10px);
                                    }
                                }

                                @keyframes fade-in-up {
                                    from {
                                        opacity: 0;
                                        transform: translateY(20px);
                                    }
                                    to {
                                        opacity: 1;
                                        transform: translateY(0);
                                    }
                                }

                                @keyframes fade-in {
                                    from {
                                        opacity: 0;
                                    }
                                    to {
                                        opacity: 1;
                                    }
                                }

                                @keyframes bounce-horizontal {
                                    0%, 100% {
                                        transform: translateX(0);
                                    }
                                    50% {
                                        transform: translateX(5px);
                                    }
                                }
                            `}</style>
                    </div>
                </> : <>
                    <div className="flex flex-col w-full h-full bg-white">
                        {/* Header - responsive height */}
                        <div
                            className="flex items-center justify-between flex-shrink-0"
                            style={{
                                padding: 'var(--spacing-sm) var(--spacing-md)',
                                minHeight: '52px',
                                background: 'linear-gradient(to bottom, var(--color-surface), var(--color-gray-50))',
                                borderBottom: '1px solid var(--color-border-light)',
                                boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)'
                            }}
                        >
                            <div className="flex items-center flex-wrap" style={{ gap: 'var(--spacing-md)' }}>
                                {/* Instrument name */}
                                <div className="flex-shrink-0" style={{
                                    padding: 'var(--spacing-xs) var(--spacing-md)',
                                    backgroundColor: 'var(--color-primary-50)',
                                    borderRadius: 'var(--radius-lg)',
                                    border: '1px solid var(--color-primary-100)'
                                }}>
                                    <span style={{
                                        fontSize: '0.9375rem',
                                        fontWeight: '600',
                                        color: 'var(--color-primary-700)'
                                    }}>{inputs[selectedChart]?.instrument}</span>
                                </div>

                                {/* Timeframe dropdown */}
                                <Dropdown
                                    align="left"
                                    minWidth="160px"
                                    options={TIMEFRAME_OPTIONS.map(tf => ({
                                        id: tf.value,
                                        label: tf.label,
                                        selected: inputs[selectedChart]?.timeframe === tf.value
                                    }))}
                                    onSelect={(timeframeValue) => {
                                        dispatch(setChartTimeframe({
                                            chartIndex: selectedChart,
                                            timeframe: timeframeValue as Timeframe
                                        }));
                                    }}
                                    trigger={
                                        <button
                                            style={{
                                                padding: 'var(--spacing-sm) var(--spacing-md)',
                                                borderRadius: 'var(--radius-md)',
                                                border: '1px solid var(--color-border-light)',
                                                backgroundColor: 'var(--color-surface)',
                                                color: 'var(--color-text-primary)',
                                                fontSize: '0.875rem',
                                                cursor: 'pointer',
                                                transition: 'var(--transition-base)',
                                                minWidth: '120px',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'space-between',
                                                gap: 'var(--spacing-sm)'
                                            }}
                                            onMouseEnter={(e) => {
                                                e.currentTarget.style.backgroundColor = 'var(--color-gray-50)';
                                            }}
                                            onMouseLeave={(e) => {
                                                e.currentTarget.style.backgroundColor = 'var(--color-surface)';
                                            }}
                                        >
                                            <span>{TIMEFRAME_OPTIONS.find(t => t.value === inputs[selectedChart]?.timeframe)?.label}</span>
                                            <svg
                                                style={{
                                                    width: '14px',
                                                    height: '14px',
                                                    transition: 'transform 200ms ease-in-out'
                                                }}
                                                fill="none"
                                                stroke="currentColor"
                                                viewBox="0 0 24 24"
                                            >
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                            </svg>
                                        </button>
                                    }
                                />

                                {/* Studies dropdown */}
                                <Dropdown
                                    align="left"
                                    minWidth="200px"
                                    multiSelect={true}
                                    options={STUDY_OPTIONS.map(study => ({
                                        id: study.id,
                                        label: study.name,
                                        selected: inputs[selectedChart]?.studies?.some(s => s.id === study.id) ?? false
                                    }))}
                                    onSelect={(studyId) => {
                                        const isSelected = inputs[selectedChart]?.studies?.some(s => s.id === studyId) ?? false;
                                        if (!isSelected) {
                                            const study = STUDY_OPTIONS.find(s => s.id === studyId);
                                            if (study) {
                                                dispatch(addChartStudy({
                                                    chartIndex: selectedChart,
                                                    study: { id: study.id, name: study.name }
                                                }));
                                            }
                                        } else {
                                            dispatch(removeChartStudy({
                                                chartIndex: selectedChart,
                                                studyId
                                            }));
                                        }
                                    }}
                                    trigger={
                                        <button
                                            style={{
                                                padding: 'var(--spacing-sm) var(--spacing-md)',
                                                borderRadius: 'var(--radius-md)',
                                                border: '1px solid var(--color-border-light)',
                                                backgroundColor: 'var(--color-surface)',
                                                color: 'var(--color-text-primary)',
                                                fontSize: '0.875rem',
                                                cursor: 'pointer',
                                                transition: 'var(--transition-base)',
                                                minWidth: '100px',
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 'var(--spacing-sm)'
                                            }}
                                            onMouseEnter={(e) => {
                                                e.currentTarget.style.backgroundColor = 'var(--color-gray-50)';
                                            }}
                                            onMouseLeave={(e) => {
                                                e.currentTarget.style.backgroundColor = 'var(--color-surface)';
                                            }}
                                        >
                                            Studies {(inputs[selectedChart]?.studies?.length ?? 0) > 0 && `(${inputs[selectedChart].studies.length})`}
                                        </button>
                                    }
                                />

                                {/* Save template button */}
                                <button
                                    onClick={() => setDefaultTemplate(inputs[selectedChart])}
                                    title="Save as Default Layout"
                                    className="hidden md:flex"
                                    style={{
                                        padding: 'var(--spacing-xs) var(--spacing-sm)',
                                        borderRadius: 'var(--radius-md)',
                                        transition: 'var(--transition-base)',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        backgroundColor: 'transparent',
                                        border: '1px solid var(--color-border-light)',
                                        cursor: 'pointer',
                                        color: 'var(--color-text-secondary)'
                                    }}
                                    onMouseEnter={(e) => {
                                        e.currentTarget.style.backgroundColor = 'var(--color-gray-100)';
                                        e.currentTarget.style.color = 'var(--color-primary-600)';
                                    }}
                                    onMouseLeave={(e) => {
                                        e.currentTarget.style.backgroundColor = 'transparent';
                                        e.currentTarget.style.color = 'var(--color-text-secondary)';
                                    }}
                                >
                                    <BiCheckboxChecked size={20} />
                                </button>

                                {/* Option Chain link */}
                                <button
                                    onClick={() => {
                                        const isDev = typeof import.meta !== 'undefined' && (import.meta as any).env?.DEV;
                                        const baseUrl = isDev ? 'http://localhost:5173' : window.location.origin;
                                        window.open(`${baseUrl}/optionchain?symbol=${instrument}`, "_blank");
                                    }}
                                    title="Open Option Chain"
                                    className="hidden lg:flex"
                                    style={{
                                        padding: 'var(--spacing-xs) var(--spacing-md)',
                                        borderRadius: 'var(--radius-md)',
                                        transition: 'var(--transition-base)',
                                        backgroundColor: 'transparent',
                                        border: '1px solid var(--color-border-light)',
                                        cursor: 'pointer',
                                        color: 'var(--color-text-secondary)',
                                        fontSize: '0.8125rem',
                                        fontWeight: '500'
                                    }}
                                    onMouseEnter={(e) => {
                                        e.currentTarget.style.backgroundColor = 'var(--color-gray-100)';
                                        e.currentTarget.style.color = 'var(--color-primary-600)';
                                    }}
                                    onMouseLeave={(e) => {
                                        e.currentTarget.style.backgroundColor = 'transparent';
                                        e.currentTarget.style.color = 'var(--color-text-secondary)';
                                    }}
                                >
                                    Option Chain
                                </button>
                            </div>

                            {/* Right side - screen split */}
                            <div className="flex items-center flex-shrink-0">
                                {/* Screen split dropdown */}
                                <Dropdown
                                    align="right"
                                    minWidth="140px"
                                    options={[
                                        { id: 'single', label: 'Single', icon: <SingleScreenIcon /> },
                                        { id: 'double', label: 'Double', icon: <DoubleScreenIcon /> },
                                        { id: 'triple', label: 'Triple', icon: <TripleScreenIcon /> }
                                    ]}
                                    onSelect={(layoutId) => {
                                        if (layoutId === 'single') {
                                            dispatch(setScreenLayout({ index: 0, cols: 1, rows: 1 }));
                                        } else if (layoutId === 'double') {
                                            dispatch(setScreenLayout({ index: 1, cols: 2, rows: 1 }));
                                            dispatch(duplicateChartToIndex({ sourceIndex: selectedChart, targetIndex: 1 }));
                                        } else if (layoutId === 'triple') {
                                            dispatch(setScreenLayout({ index: 2, cols: 3, rows: 1 }));
                                            dispatch(duplicateChartToIndex({ sourceIndex: selectedChart, targetIndex: 1 }));
                                            dispatch(duplicateChartToIndex({ sourceIndex: selectedChart, targetIndex: 2 }));
                                        }
                                    }}
                                    trigger={
                                        <button
                                            title="Screen Layout"
                                            style={{
                                                padding: 'var(--spacing-xs) var(--spacing-sm)',
                                                borderRadius: 'var(--radius-md)',
                                                border: '1px solid var(--color-border-light)',
                                                backgroundColor: 'var(--color-surface)',
                                                cursor: 'pointer',
                                                transition: 'var(--transition-base)',
                                                display: 'flex',
                                                alignItems: 'center',
                                                color: 'var(--color-text-secondary)'
                                            }}
                                            onMouseEnter={(e) => {
                                                e.currentTarget.style.backgroundColor = 'var(--color-gray-50)';
                                                e.currentTarget.style.borderColor = 'var(--color-primary-300)';
                                                e.currentTarget.style.color = 'var(--color-primary-600)';
                                            }}
                                            onMouseLeave={(e) => {
                                                e.currentTarget.style.backgroundColor = 'var(--color-surface)';
                                                e.currentTarget.style.borderColor = 'var(--color-border-light)';
                                                e.currentTarget.style.color = 'var(--color-text-secondary)';
                                            }}
                                        >
                                            {getScreenIcon(screenSplitIndex)}
                                        </button>
                                    }
                                />
                            </div>
                        </div>

                        {/* Chart grid - flexible height */}
                        <div
                            ref={containerRef}
                            className="flex-1 w-full grid overflow-hidden"
                            style={{
                                gridTemplateColumns: `repeat(${cols}, 1fr)`,
                                gridTemplateRows: `repeat(${rows}, 1fr)`,
                                width: width,
                                height: height
                            }}
                        >
                            {Array.from({ length: cols * rows }).map((_, index) => (
                                <div
                                    key={index}
                                    className="cursor-pointer"
                                    onClick={() => dispatch(setSelectedChart(index))}
                                >
                                    <ChartComponent
                                        selected={selectedChart === index}
                                        instrument={inputs[index]?.instrument || inputs[selectedChart]?.instrument || "SBIN"}
                                        timeframe={inputs[index]?.timeframe || '1day'}
                                        studies={inputs[index]?.studies || []}
                                        width={cellSize.width}
                                        height={cellSize.height}
                                    />
                                </div>
                            ))}
                        </div>

                        {/* Footer - responsive height */}
                        <div
                            className="bg-gray-50 border-t border-gray-300 flex-shrink-0"
                            style={{
                                padding: 'var(--spacing-xs) var(--spacing-md)',
                                minHeight: '32px'
                            }}
                        >
                            <div className="flex items-center justify-between h-full">
                                <div className="text-xs sm:text-sm text-gray-800">
                                    {new Date().toLocaleTimeString()}
                                </div>
                                {/* Add more footer items here if needed */}
                            </div>
                        </div>
                    </div>
                </>
            }
        </>

    );
}



export default ChartWrapper


